import { useCloned } from '@vueuse/core';
import type { APIErrorResponse, JsonApiResponse } from '~/interfaces/api/main';

interface RoundingOptions {
  precision?: number;
  mode?: 'ceil' | 'floor' | 'trunc';
  hideZeroDecimals?: boolean;
  trimSpace?: boolean;
}

export function debug (context: string, value: unknown): void {
  if (import.meta.dev) {
    console.debug('DEBUG: ', context, value);
  }
}

// Utility: Format a date string
export const formatDate = (
  dateString?: string,
  includeTime: boolean = true,
): string | undefined => {
  if (!dateString) { return undefined; }

  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
    ...(includeTime && {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    }),
  };

  return new Intl.DateTimeFormat('default', options).format(date);
};

export const formatDateTime = (dateString: string): string => new Date(dateString)
  .toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Paris' });

export const formatTime = (
  date: Date | null,
): string => {
  if (!date) { return ''; }
  const dateObj = new Date(date);
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  return `${hours}h${minutes}`;
};

export const formatMinutesToHours = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) {
    return '';
  }
  if (minutes === 0) {
    return '0h';
  }
  if (minutes % 60 === 0) {
    return `${minutes / 60}h`;
  }
  return `${minutes}m`;
};

export function formatDateForAPI (date: Date): string {
  return new Intl.DateTimeFormat('fr-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

// Local datetime in HTML5 format (YYYY-MM-DDTHH:mm:ss) — matches Symfony DateTimeType single_text.
// Using getHours/Minutes/Seconds (local) instead of toISOString (UTC) avoids the -2h drift on save.
// Throws on Invalid Date to mirror the fail-fast behavior of toISOString and prevent
// silent payload corruption ("NaN-NaN-NaNT...").
export function formatDateTimeForAPI (date: Date): string {
  if (Number.isNaN(date.getTime())) {
    throw new RangeError('formatDateTimeForAPI: Invalid Date');
  }
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

export const parseDateTimeFromAPI = (value: string | null | undefined): Date | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  if (!normalized) {
    return null;
  }
  const candidate = normalized.includes('T') ? normalized : normalized.replace(' ', 'T');
  const parsed = new Date(candidate);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

// Utility: Download a file by URL
export const downloadByUrl = async (url: string, filename: string): Promise<void> => {
  try {
    const response = await fetch(url);
    if (!response.ok) { throw new Error(`HTTP error! status: ${response.status}`); }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error: any) {
    useToastService().add({
      severity: 'error',
      summary: 'Téléchargement',
      detail: error.message ?? 'Erreur inconnue lors du téléchargement.',
    });
  }
};

// Utility: Generate a dark color from a seed integer
export const generateDarkColorFromInt = (seed: number): string => {
  const random = (x: number): number => (Math.sin(x) + 1) * 128;
  const red = Math.floor(random(seed) % 128);
  const green = Math.floor(random(seed + 1) % 128);
  const blue = Math.floor(random(seed + 2) % 128);
  return `rgb(${red},${green},${blue})`;
};

// Type Guard: Check if data is an APIErrorResponse
export function isAPIErrorResponse (data: any): data is APIErrorResponse {
  return data && Array.isArray(data.errors) && data.errors.length > 0;
}

// Type Guard: Check if data is a JsonApiResponse
export function isJsonApiResponse (data: any): data is JsonApiResponse {
  return data && 'data' in data;
}

export function convertAmountToFloat (value: string | number | null): number {
  if (value === null || value === undefined) {
    return 0.0; // Default value for null or undefined
  }

  const amount = typeof value === 'string'
    ? Number.parseFloat(String(value).replace(',', '.'))
    : value;

  return Number.isNaN(amount) ? 0.0 : amount;
}

export function normalizeRouteParam (param: string | string[] | null | undefined): string | null {
  if (param === undefined || param === null) {
    return null;
  }
  const value = Array.isArray(param) ? param[0] : param;
  return value !== undefined && value !== null ? value.toString() : null;
}

// Utility: Format a number, string, or null as currency
export function formatCurrency (value: string | number | null, options?: RoundingOptions | null): string {
  let numericValue = convertAmountToFloat(value); // Ensure conversion handles null internally
  const roundingMode = options?.mode;
  const roundingFn = roundingMode ? Math[roundingMode] : undefined;

  if (typeof roundingFn === 'function') {
    numericValue = roundingFn(numericValue);
  }

  return numericValue.toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: options?.precision ?? 2,
    maximumFractionDigits: options?.precision ?? 2,
  });
}

export function formatCurrencyOrDash (value: string | number | null | undefined, options: RoundingOptions | null = null): string {
  if (value === 0 || value === null || value === undefined) {
    return '-';
  }
  return formatCurrency(value, options);
}

// Utility: Format a number as a percentage
export function formatRate (value: number | null | undefined, options: RoundingOptions | null = { precision: 2 }): string {
  let numericValue = convertAmountToFloat(value);
  const roundingMode = options?.mode;
  const roundingFn = roundingMode ? Math[roundingMode] : undefined;

  if (typeof roundingFn === 'function') {
    numericValue = roundingFn(numericValue);
  }

  let precision = options?.precision ?? 2;
  const roundedForPrecision = Number.isFinite(numericValue)
    ? Number(numericValue.toFixed(precision))
    : numericValue;
  const shouldHideZeroDecimals = Boolean(options?.hideZeroDecimals) && Number.isInteger(roundedForPrecision);
  if (shouldHideZeroDecimals) {
    precision = 0;
  }

  return `${numericValue.toLocaleString('fr-FR', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })}${options?.trimSpace ? '%' : ' %'}`;
}

export function formatRateFromDecimal (value: number | null | undefined, options: RoundingOptions | null = { precision: 2 }): string {
  return formatRate(value !== null && value !== undefined ? value * 100 : value, options);
}

// Utility: Format a value based on its type
export function formatValue (value: any): string | number {
  if (!value?.value) { return 'Non Renseigné'; }

  if (value.type === 'currency') {
    const numberValue = Number(value.value);
    return Number.isNaN(numberValue) ? value : formatCurrency(numberValue);
  }

  if (value.type === 'boolean') {
    return value.value ? 'Succès de la transaction' : 'Échec de la transaction';
  }

  return value.value;
}

// Utility: Fetch a file from a URL
export async function getFileFromUrl (
  url: string,
  name: string,
  defaultType: string = 'image/jpeg',
  options: { signal?: AbortSignal } = {},
): Promise<File | undefined> {
  try {
    const response = await fetch(url, { signal: options.signal });
    // Aucun statut d'erreur ne produit de fichier : le corps d'un 403 est un XML d'erreur.
    if (!response.ok) {
      if (response.status !== 404) { console.error(`HTTP error! status: ${response.status}`); }
      return undefined;
    }
    const blob = await response.blob();
    return new File([blob], name, { type: blob.type || defaultType });
  } catch (error: any) {
    if (error.name !== 'AbortError') { console.error(error); }
  }
}

// Utility: Shorten a string to "xxxx...xxxx"
export function shortenString (str: string): string {
  if (!str) { return ''; }
  if (str.length <= 8) { return str; }
  return `${str.slice(0, 4)}...${str.slice(-4)}`;
}

export function clone (dataToSave: any): any {
  const { cloned } = useCloned(dataToSave);
  return cloned.value;
}

export function handlePlural (base: string, count: number, suffix: string = 's'): string {
  return count > 1 ? base + suffix : base;
}

export function waitForPageStability (
  rootEl: HTMLElement = document.body,
  debounceMs: number = 200,
  maxWaitMs: number = 1000,
): Promise<void> {
  return new Promise<void>((resolve) => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let activeTransitions = 0;
    let resolved = false;

    const done = (): void => {
      if (resolved) { return; }
      resolved = true;

      observer.disconnect();
      rootEl.removeEventListener('transitionstart', onStart, true);
      rootEl.removeEventListener('transitionend', onEnd, true);
      rootEl.removeEventListener('animationstart', onStart, true);
      rootEl.removeEventListener('animationend', onEnd, true);

      if (debounceTimer) { clearTimeout(debounceTimer); }
      resolve();
    };

    const finishIfStable = (): void => {
      if (activeTransitions > 0) { return; }
      if (debounceTimer) { clearTimeout(debounceTimer); }
      debounceTimer = setTimeout(() => {
        done();
      }, debounceMs);
    };

    const onStart = (): void => {
      activeTransitions++;
      if (debounceTimer) { clearTimeout(debounceTimer); }
    };

    const onEnd = (): void => {
      activeTransitions = Math.max(0, activeTransitions - 1);
      finishIfStable();
    };

    const observer = new MutationObserver(() => {
      if (debounceTimer) { clearTimeout(debounceTimer); }
      finishIfStable();
    });

    observer.observe(rootEl, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    rootEl.addEventListener('transitionstart', onStart, true);
    rootEl.addEventListener('transitionend', onEnd, true);
    rootEl.addEventListener('animationstart', onStart, true);
    rootEl.addEventListener('animationend', onEnd, true);

    // Fallback: force-resolve if things go wrong
    setTimeout(() => done(), maxWaitMs);

    // Kickstart stability check in case nothing happens
    finishIfStable();
  });
}

export function areArraysDifferent<T> (a: T[], b: T[]): boolean {
  return a.length !== b.length || a.some((val, i) => val !== b[i]);
}

export function capitalize (str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getValidation (v$: unknown): { $validate: () => Promise<boolean> } | null {
  if (!v$) { return null; }
  if (typeof (v$ as any).$validate === 'function') { return v$ as any; }
  if ((v$ as any).value?.$validate) { return (v$ as any).value; }
  return null;
}

export function parseNumberOrNull (value: unknown): number | null {
  if (value === null || value === undefined || value === '') { return null; }
  const num = Number(typeof value === 'string' ? value.replace(',', '.') : value);
  return Number.isFinite(num) ? num : null;
}

// Utility: format an IBAN number
export function formatIban (iban: string | null): string {
  if (!iban) {
    return '';
  }
  // Remove all non-alphanumeric characters, convert to uppercase, and group by 4 characters
  const cleaned = iban.replace(/[^A-Z0-9]/gi, '').toUpperCase();

  return cleaned.match(/.{1,4}/g)?.join(' ');
}

export const parseDurationToMinutes = (value: string): number | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  const hoursMinutesMatch = normalized.match(/^(\d+(?:[.,]\d+)?)\s*h(?:\s*(\d{1,2}))?\s*m?$/);
  if (hoursMinutesMatch) {
    const hoursValue = Number(hoursMinutesMatch[1].replace(',', '.'));
    if (Number.isNaN(hoursValue)) {
      return null;
    }
    const minutesPart = hoursMinutesMatch[2];
    if (minutesPart !== undefined) {
      const minutesValue = Number(minutesPart);
      if (Number.isNaN(minutesValue) || minutesValue >= 60) {
        return null;
      }
      return Math.round(Math.trunc(hoursValue) * 60 + minutesValue);
    }
    return Math.round(hoursValue * 60);
  }

  const clockMatch = normalized.match(/^(\d+)\s*:\s*(\d{1,2})$/);
  if (clockMatch) {
    const hoursValue = Number(clockMatch[1]);
    const minutesValue = Number(clockMatch[2]);
    if (Number.isNaN(hoursValue) || Number.isNaN(minutesValue) || minutesValue >= 60) {
      return null;
    }
    return Math.round(hoursValue * 60 + minutesValue);
  }

  const minutesWithUnitMatch = normalized.match(/^(\d+(?:[.,]\d+)?)\s*m$/);
  if (minutesWithUnitMatch) {
    const minutesValue = Number(minutesWithUnitMatch[1].replace(',', '.'));
    if (Number.isNaN(minutesValue)) {
      return null;
    }
    return Math.round(minutesValue);
  }

  const minutesNoUnitMatch = normalized.match(/^(\d+)$/);
  if (minutesNoUnitMatch) {
    const minutesValue = Number(minutesNoUnitMatch[1]);
    if (Number.isNaN(minutesValue)) {
      return null;
    }
    return Math.round(minutesValue);
  }

  return null;
};
